import { Button } from '@/components/ui/button';
import { ApiError, api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { formatRelative } from '@/routes/admin/llm/EndpointDetail';
import type { LlmConcurrencySlot, LlmEndpoint } from '@innermate/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Modal } from './Modal';

interface Props {
  open: boolean;
  onClose: () => void;
  /** 엔드포인트 표시명 룩업 + (있으면) 특정 엔드포인트로 필터. */
  endpoints: LlmEndpoint[];
  filterEndpointId: string | null;
}

/**
 * 시스템 작업 라벨 → 한국어 표시. 미지정 라벨은 raw 그대로 노출(폴백 안전).
 * 라벨은 provider.ts gate 의 systemTask 와 1:1 (server `getAdapterFor({ systemTask: ... })`).
 */
const SYSTEM_TASK_LABELS: Record<string, string> = {
  'simple-completion': '단발 LLM 호출',
  'chat-compaction': '채팅 압축',
  'wiki-text-cleanup': '위키 텍스트 정제',
  'wiki-curate': '위키 큐레이트',
  'admin-skill-creator': '스킬 AI 생성',
  'wiki-ingest:image': '위키 인제스트(이미지)',
  'wiki-ingest:ocr': '위키 인제스트(OCR)',
  'wiki-ingest:meta': '위키 인제스트(메타)',
  'wiki-ingest:chunk': '위키 인제스트(청크 페이지화)',
  'wiki-health-check': '위키 건강검진',
  'skill-audit': '스킬 점검',
  'data-source-descriptions': 'DS 설명 생성',
  classifier: '역할 분류기',
  'skill-tool': '스킬 호출(중첩)',
};

/** 미래 시각까지 남은 시간 (formatRelative 는 과거 전용이라 만료까지를 따로 표기). */
function formatUntil(iso: string): string {
  const sec = Math.floor((new Date(iso).getTime() - Date.now()) / 1000);
  if (sec <= 0) return '만료됨';
  if (sec < 60) return `${sec}초 후`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}분 후`;
  return `${Math.floor(min / 60)}시간 후`;
}

/**
 * LLM 동시성 슬롯 단위 상세 — 안 풀리는 슬롯의 holder/획득/만료를 보고 강제 해제까지.
 * 집계(ConcurrencyInline)로는 진단이 안 되므로 개별 행을 노출한다. 열려있는 동안만 5초 폴링.
 */
export function ConcurrencySlotsModal({ open, onClose, endpoints, filterEndpointId }: Props) {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'llm-concurrency', 'slots'],
    queryFn: () => api.adminLlmConcurrencySlots(),
    enabled: open,
    refetchInterval: 5_000,
    refetchOnMount: 'always',
  });

  const releaseMut = useMutation({
    mutationFn: (token: string) => api.adminLlmConcurrencyRelease(token),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'llm-concurrency', 'slots'] });
      qc.invalidateQueries({ queryKey: ['admin', 'llm-concurrency'], exact: true });
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? err.message : '슬롯 강제 해제에 실패했습니다.');
    },
  });

  const nameOf = (id: string) => {
    const ep = endpoints.find((e) => e.id === id);
    return ep ? (ep.displayName ?? ep.name) : id;
  };

  const allSlots = data?.slots ?? [];
  const slots = filterEndpointId
    ? allSlots.filter((s) => s.endpointId === filterEndpointId)
    : allSlots;

  // 엔드포인트별 그룹 (조회 결과가 이미 endpoint_id, acquired_at 순 정렬).
  const groups = new Map<string, LlmConcurrencySlot[]>();
  for (const s of slots) {
    const arr = groups.get(s.endpointId) ?? [];
    arr.push(s);
    groups.set(s.endpointId, arr);
  }

  const handleRelease = (slot: LlmConcurrencySlot) => {
    if (
      window.confirm(
        `이 슬롯을 강제로 해제할까요?\n\n점유자: ${slot.holder}\n주의: 아직 사용 중인 요청의 슬롯이면 동시 사용 한도가 일시적으로 초과될 수 있습니다.`,
      )
    ) {
      setError(null);
      releaseMut.mutate(slot.token);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="슬롯 현황 상세"
      description={
        filterEndpointId
          ? `${nameOf(filterEndpointId)} — 현재 점유 중인 슬롯`
          : '엔드포인트별 현재 점유 중인 슬롯. 안 풀리는 슬롯은 행에서 직접 강제 해제할 수 있습니다.'
      }
      size="xl"
    >
      {error ? (
        <div
          role="alert"
          className="mb-4 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive"
        >
          {error}
        </div>
      ) : null}

      {isLoading ? (
        <p className="text-sm text-foreground-muted">불러오는 중…</p>
      ) : slots.length === 0 ? (
        <p className="py-10 text-center text-sm text-foreground-muted">
          현재 점유 중인 슬롯이 없습니다.
        </p>
      ) : (
        <div className="space-y-6">
          {[...groups.entries()].map(([endpointId, rows]) => (
            <div key={endpointId}>
              <div className="mb-2 flex items-baseline gap-2">
                <h3 className="text-sm font-semibold">{nameOf(endpointId)}</h3>
                <span className="text-xs text-foreground-muted">{rows.length}개 점유</span>
              </div>
              <div className="overflow-hidden rounded-xl border border-border">
                <table className="w-full text-sm">
                  <thead className="bg-surface-muted/40 text-xs uppercase tracking-wider text-foreground-muted">
                    <tr>
                      <Th>종류</Th>
                      <Th>점유자</Th>
                      <Th>획득</Th>
                      <Th>만료</Th>
                      <Th align="right">액션</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((s) => (
                      <tr key={s.token} className="border-t border-border">
                        <td className="px-3 py-2">
                          <span
                            className={cn(
                              'inline-flex items-center rounded-sm px-1.5 h-5 text-[10px] uppercase tracking-wider',
                              s.kind === 'system'
                                ? 'border border-border text-foreground-muted'
                                : 'bg-accent/15 text-accent',
                            )}
                          >
                            {s.kind === 'system' ? '시스템' : '사용자'}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-xs">
                          {/*
                            actor 와 systemTask 는 공존 가능 (예: sub-skill 호출 — 어느 사용자 턴의
                            system kind 슬롯). 따라서 if/elif 아닌 additive 로 둘 다 표시한다.
                          */}
                          {s.actorName || s.actorEmail ? (
                            <>
                              <div className="font-medium">{s.actorName ?? '—'}</div>
                              {s.actorEmail ? (
                                <div className="text-foreground-muted">{s.actorEmail}</div>
                              ) : null}
                            </>
                          ) : s.actorUserId ? (
                            // userId 는 있는데 users 조회 실패 (삭제됨 등) — 진단을 위해 raw id 노출.
                            <div className="text-foreground-muted">
                              알 수 없는 사용자 ({s.actorUserId.slice(0, 8)}…)
                            </div>
                          ) : null}
                          {s.systemTask ? (
                            <div
                              className={
                                s.actorName || s.actorEmail || s.actorUserId ? 'mt-0.5' : ''
                              }
                            >
                              <span className="font-medium">
                                {SYSTEM_TASK_LABELS[s.systemTask] ?? s.systemTask}
                              </span>
                              <span className="ml-1 font-mono text-[10px] text-foreground-muted">
                                {s.systemTask}
                              </span>
                            </div>
                          ) : null}
                          {!s.actorName && !s.actorEmail && !s.actorUserId && !s.systemTask ? (
                            // 라벨/사용자 둘 다 없는 legacy / 누락 호출부 fallback.
                            <div className="text-foreground-muted">시스템 작업</div>
                          ) : null}
                          <div className="mt-0.5 font-mono text-[11px] text-foreground-muted break-all">
                            {s.holder}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-xs text-foreground-muted">
                          {formatRelative(s.acquiredAt)}
                        </td>
                        <td className="px-3 py-2 text-xs">
                          {s.stale ? (
                            <span className="font-medium text-destructive">만료됨</span>
                          ) : (
                            <span className="text-foreground-muted">
                              {formatUntil(s.expiresAt)}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleRelease(s)}
                            disabled={releaseMut.isPending}
                          >
                            강제 해제
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

function Th({ children, align = 'left' }: { children: React.ReactNode; align?: 'left' | 'right' }) {
  return (
    <th
      scope="col"
      className={cn('px-3 py-2 font-medium', align === 'right' ? 'text-right' : 'text-left')}
    >
      {children}
    </th>
  );
}
